(() => {
  'use strict';

  const bubble = document.querySelector('[data-ct-live-proof]');
  if (!bubble || bubble.dataset.ctLiveProofReady === 'true') return;
  bubble.dataset.ctLiveProofReady = 'true';

  const feedUrl = bubble.dataset.feedUrl;
  const link = bubble.querySelector('[data-proof-link]');
  const image = bubble.querySelector('[data-proof-image]');
  const fallback = bubble.querySelector('[data-proof-fallback]');
  const buyerCopy = bubble.querySelector('[data-proof-buyer]');
  const copy = bubble.querySelector('[data-proof-copy]');
  const close = bubble.querySelector('.ct-live-proof__close');
  if (!feedUrl || !link || !image || !fallback || !buyerCopy || !copy || !close) return;

  const CLOSE_SESSION_KEY = 'ct_live_proof_closed_session_v5';
  const CLOSE_UNTIL_KEY = 'ct_live_proof_closed_until_v5';
  const LAST_HANDLE_KEY = 'ct_live_proof_last_handle_v3';
  const SHOW_COUNT_KEY = 'ct_live_proof_show_count_v5';
  const CLOSE_MS = 24 * 60 * 60 * 1000;
  const FIRST_DELAY_MS = 4000;
  const VISIBLE_MS = 5800;
  const REST_MS = 21000;
  const RESUME_MS = 2400;
  const MAX_SHOWS = 3;
  const MAX_FEED_BYTES = 16384;
  const MAX_RECORDS = 12;
  const MAX_FEED_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,127}$/;
  const BUYER_PATTERN = /^\p{L}[\p{L}\p{M}'-]{1,23} \p{L}\.$/u;
  const CITY_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} '-]{1,31}$/u;
  const BLOCKING_QUERY = [
    '[data-ct-cw-popup]:not([hidden])',
    '[data-ct-cw-bubble]:not([hidden])',
    'cart-drawer.active',
    '.drawer.active',
    'dialog[open]',
    '.product-media-modal[open]',
    'quick-add-modal[open]',
    'menu-drawer details[open]',
    'header-drawer details[open]',
    '[data-ct-checkout-transition]:not([hidden])'
  ].join(',');
  const BLOCKING_NODES = [
    '[data-ct-cw-popup]',
    '[data-ct-cw-bubble]',
    'cart-drawer',
    '.drawer',
    'dialog',
    '.product-media-modal',
    'quick-add-modal',
    'menu-drawer details',
    'header-drawer details',
    '[data-ct-checkout-transition]'
  ].join(',');

  const firstTarget = performance.now() + FIRST_DELAY_MS;
  let records = [];
  let closed = isClosed();
  let shownCount = readSessionInteger(SHOW_COUNT_KEY);
  let visible = false;
  let blocked = hasBlockingUi();
  let nextTimer = 0;
  let hideTimer = 0;
  let finishTimer = 0;
  let observerFrame = 0;
  let interactionTimer = 0;

  setInteractive(false);
  if (closed || shownCount >= MAX_SHOWS) return;

  const loadFeed = () => {
    if (closed || shownCount >= MAX_SHOWS) return;

    fetch(feedUrl, { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Recent purchase feed HTTP ${response.status}`);
        const raw = await response.text();
        const bytes = typeof TextEncoder === 'function' ? new TextEncoder().encode(raw).byteLength : raw.length;
        if (bytes > MAX_FEED_BYTES) throw new Error('Recent purchase feed exceeds size limit');
        return {
          feed: JSON.parse(raw),
          serverNow: Date.parse(response.headers.get('date') || '') || Date.now()
        };
      })
      .then(({ feed, serverNow }) => {
        if (feed?.schema !== 2 || feed?.source !== 'last-200-orders-first-name-last-initial-city') return;
        const generatedAt = Date.parse(feed.generatedAt || '');
        if (
          !Number.isFinite(generatedAt) ||
          generatedAt > serverNow + MAX_FUTURE_SKEW_MS ||
          serverNow - generatedAt > MAX_FEED_AGE_MS ||
          !Array.isArray(feed.records) ||
          feed.records.length > MAX_RECORDS
        ) return;

        records = feed.records.map(normalizeRecord).filter(Boolean);
        if (!records.length || closed) return;
        schedule(Math.max(0, firstTarget - performance.now()));
      })
      .catch(() => {
        records = [];
        hide(false, true);
      });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadFeed, { timeout: 2500 });
  } else {
    window.setTimeout(loadFeed, 1500);
  }

  const uiObserver = new MutationObserver(scheduleBlockingUiCheck);
  if (document.body) {
    uiObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    observeBlockingNodes();
  }

  document.addEventListener('click', handlePossibleUiInteraction, true);
  document.addEventListener('toggle', handlePossibleUiInteraction, true);
  document.addEventListener('visibilitychange', scheduleBlockingUiCheck);

  function normalizeRecord(record) {
    if (
      !record ||
      typeof record.buyer !== 'string' ||
      !BUYER_PATTERN.test(record.buyer.trim()) ||
      record.buyer.length > 28 ||
      typeof record.city !== 'string' ||
      !CITY_PATTERN.test(record.city.trim()) ||
      typeof record.product !== 'string' ||
      !record.product.trim() ||
      record.product.length > 120 ||
      typeof record.handle !== 'string' ||
      !HANDLE_PATTERN.test(record.handle) ||
      record.url !== `/products/${record.handle}` ||
      typeof record.image !== 'string' ||
      record.image.length > 1024 ||
      (record.imageAlt != null && (typeof record.imageAlt !== 'string' || record.imageAlt.length > 180))
    ) return null;

    try {
      const imageUrl = new URL(record.image, window.location.origin);
      const allowedHost = imageUrl.hostname === 'cdn.shopify.com' || imageUrl.hostname === window.location.hostname;
      if (imageUrl.protocol !== 'https:' || !allowedHost) return null;
    } catch (error) {
      return null;
    }

    return {
      buyer: record.buyer.trim(),
      city: record.city.trim(),
      product: record.product.trim(),
      handle: record.handle,
      url: record.url,
      image: record.image,
      imageAlt: record.imageAlt?.trim() || record.product.trim()
    };
  }

  function schedule(delay) {
    window.clearTimeout(nextTimer);
    if (closed || shownCount >= MAX_SHOWS) return;
    nextTimer = window.setTimeout(show, Math.max(0, delay));
  }

  function clearTimers() {
    window.clearTimeout(nextTimer);
    window.clearTimeout(hideTimer);
    window.clearTimeout(finishTimer);
  }

  function chooseRecord() {
    if (!records.length) return null;
    const lastHandle = readSession(LAST_HANDLE_KEY);
    const alternatives = records.filter((record) => record.handle !== lastHandle);
    const pool = alternatives.length ? alternatives : records;
    return pool[secureRandomIndex(pool.length)];
  }

  function show() {
    clearTimers();
    if (closed || !records.length || shownCount >= MAX_SHOWS) return;
    blocked = hasBlockingUi();
    if (blocked) {
      schedule(RESUME_MS);
      return;
    }

    const record = chooseRecord();
    if (!record) return;
    writeSession(LAST_HANDLE_KEY, record.handle);

    link.href = record.url;
    buyerCopy.textContent = `${record.buyer} de ${record.city}`;
    copy.textContent = `Compró ${record.product}`;
    link.setAttribute(
      'aria-label',
      `Compra reciente ${record.buyer} de ${record.city} Compró ${record.product}`
    );
    image.hidden = true;
    fallback.hidden = false;
    image.removeAttribute('src');
    image.dataset.expectedSrc = record.image;

    bubble.hidden = false;
    setInteractive(true);
    visible = true;
    bubble.classList.remove('is-visible');
    window.requestAnimationFrame(() => {
      if (visible && !blocked) bubble.classList.add('is-visible');
      image.src = record.image;
    });

    shownCount += 1;
    writeSession(SHOW_COUNT_KEY, String(shownCount));
    hideTimer = window.setTimeout(() => hide(true), VISIBLE_MS);
  }

  function hide(scheduleNext = true, force = false) {
    window.clearTimeout(hideTimer);
    hideTimer = 0;
    if (!force && visible && (bubble.matches(':hover') || bubble.contains(document.activeElement))) {
      hideTimer = window.setTimeout(() => hide(scheduleNext), 1000);
      return;
    }

    visible = false;
    setInteractive(false);
    bubble.classList.remove('is-visible');
    window.clearTimeout(finishTimer);
    finishTimer = window.setTimeout(() => {
      bubble.hidden = true;
      finishTimer = 0;
      if (scheduleNext) schedule(REST_MS);
    }, prefersReducedMotion() ? 0 : 320);
  }

  function setInteractive(active) {
    bubble.setAttribute('aria-hidden', active ? 'false' : 'true');
    link.tabIndex = active ? 0 : -1;
    close.tabIndex = active ? 0 : -1;
  }

  image.addEventListener('load', () => {
    if (image.getAttribute('src') !== image.dataset.expectedSrc) return;
    image.hidden = false;
    fallback.hidden = true;
  });

  image.addEventListener('error', () => {
    image.hidden = true;
    fallback.hidden = false;
  });

  close.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closed = true;
    writeSession(CLOSE_SESSION_KEY, '1');
    if (preferencesStorageAllowed()) writePersistent(CLOSE_UNTIL_KEY, String(Date.now() + CLOSE_MS));
    if (bubble.contains(document.activeElement)) document.activeElement.blur();
    clearTimers();
    hide(false, true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && visible && !hasBlockingUi()) close.click();
  });

  function hasBlockingUi() {
    if (document.hidden) return true;
    const body = document.body;
    if (!body) return false;
    if (
      body.classList.contains('overflow-hidden') ||
      body.classList.contains('overflow-hidden-mobile') ||
      body.classList.contains('overflow-hidden-tablet') ||
      body.classList.contains('overflow-hidden-desktop') ||
      body.classList.contains('ct-checkout-transition-active')
    ) return true;
    return Boolean(document.querySelector(BLOCKING_QUERY));
  }

  function observeBlockingNodes() {
    document.querySelectorAll(BLOCKING_NODES).forEach((node) => {
      uiObserver.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'aria-modal']
      });
    });
  }

  function handlePossibleUiInteraction() {
    scheduleBlockingUiCheck();
    window.clearTimeout(interactionTimer);
    interactionTimer = window.setTimeout(() => {
      observeBlockingNodes();
      scheduleBlockingUiCheck();
    }, 180);
  }

  function scheduleBlockingUiCheck() {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;
      const nextBlocked = hasBlockingUi();
      if (nextBlocked === blocked) return;
      blocked = nextBlocked;
      if (blocked) {
        window.clearTimeout(nextTimer);
        hide(false, true);
      } else if (!closed && shownCount < MAX_SHOWS && records.length) {
        schedule(RESUME_MS);
      }
    });
  }

  function secureRandomIndex(length) {
    if (!Number.isInteger(length) || length < 1) return 0;
    if (window.crypto?.getRandomValues) {
      const range = 0x100000000;
      const limit = range - (range % length);
      const value = new Uint32Array(1);
      do {
        window.crypto.getRandomValues(value);
      } while (value[0] >= limit);
      return value[0] % length;
    }
    return Math.floor(Math.random() * length);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function preferencesStorageAllowed() {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.());
    } catch (error) {
      return false;
    }
  }

  function isClosed() {
    if (readSession(CLOSE_SESSION_KEY) === '1') return true;
    if (!preferencesStorageAllowed()) return false;
    const until = Number.parseInt(readPersistent(CLOSE_UNTIL_KEY), 10);
    return Number.isFinite(until) && until > Date.now();
  }

  function readSessionInteger(key) {
    const value = Number.parseInt(readSession(key), 10);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  function readSession(key) {
    try { return window.sessionStorage.getItem(key) || ''; } catch (error) { return ''; }
  }

  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (error) {}
  }

  function readPersistent(key) {
    try { return window.localStorage.getItem(key) || ''; } catch (error) { return ''; }
  }

  function writePersistent(key, value) {
    try { window.localStorage.setItem(key, value); } catch (error) {}
  }
})();


